import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Breadcrumb, Alert, Modal, Dropdown } from 'react-bootstrap';
import { useNavigate, Link, useSearchParams, useParams } from 'react-router-dom';
import { FaMagic, FaPaperPlane, FaEdit, FaCheckCircle, FaExclamationCircle, FaImage, FaTimes } from 'react-icons/fa';
import Select from 'react-select';
import { useAuth } from '../../../contexts/AuthContext';
import './Community.css';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#343a40', /* matches Bootstrap bg-dark (or we can use #212529) - wait, Form.Control bg-dark usually is #212529 or #2c2c2c. In BBR, they might use standard bs dark. Let's use #212529 */
    borderColor: state.isFocused ? '#ddb467' : '#495057',
    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(221, 180, 103, 0.25)' : 'none',
    minHeight: '48px',
    borderRadius: '8px',
    '&:hover': {
      borderColor: state.isFocused ? '#ddb467' : '#6c757d'
    }
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#343a40',
    border: '1px solid #495057',
    borderRadius: '8px',
    zIndex: 9999
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#ddb467' : state.isFocused ? '#212529' : 'transparent',
    color: state.isSelected ? '#000' : state.isFocused ? '#ddb467' : '#f8f9fa',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#ddb467',
      color: '#000'
    }
  }),
  singleValue: (base) => ({
    ...base,
    color: '#f8f9fa'
  }),
  placeholder: (base) => ({
    ...base,
    color: '#6c757d'
  }),
  input: (base) => ({
    ...base,
    color: '#f8f9fa'
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: '#495057'
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#6c757d',
    '&:hover': { color: '#f8f9fa' }
  })
};


export default function CreateThread({ isEditMode = false }) {
  const { currentUser, token } = useAuth();
  const navigate = useNavigate();
  const { editId } = useParams();
  const [searchParams] = useSearchParams();
  const defaultCategoryId = searchParams.get('c') || '';

  const [categories, setCategories] = useState([]);
  const [lakes, setLakes] = useState([]);
  
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState(defaultCategoryId);
  const [lakeId, setLakeId] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [rejectionReason, setRejectionReason] = useState(null);
  const [imageUrls, setImageUrls] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [grammarChecking, setGrammarChecking] = useState(false);

  // Feedback modal state
  const [feedbackModal, setFeedbackModal] = useState({ show: false, type: 'success', message: '', threadId: null });

  // Auto-save draft logic (only for new posts, not edits)
  useEffect(() => {
    if (isEditMode) return; // Don't load drafts in edit mode
    const savedDraft = localStorage.getItem('bbr.community.draft');
    if (savedDraft && !title && !content) {
      try {
        const draft = JSON.parse(savedDraft);
        setTitle(draft.title || '');
        setContent(draft.content || '');
        if (draft.categoryId) setCategoryId(draft.categoryId);
        if (draft.lakeId) setLakeId(draft.lakeId);
        if (draft.tags && Array.isArray(draft.tags)) {
          setTags(draft.tags);
        } else if (typeof draft.tags === 'string') {
          setTags(draft.tags.split(',').map(t => t.trim()).filter(Boolean));
        }
      } catch (e) {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  // Save to draft periodically when user types
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (title || content) {
        localStorage.setItem('bbr.community.draft', JSON.stringify({
          title, content, categoryId, lakeId, tags
        }));
      }
    }, 2000);
    return () => clearTimeout(timeout);
  }, [title, content, categoryId, lakeId, tags]);

  useEffect(() => {
    const fetchFormData = async () => {
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      try {
        setLoading(true);

        // If in edit mode, fetch the existing thread to pre-populate
        if (isEditMode && editId) {
          const threadRes = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions/${editId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).catch(() => null);
          if (threadRes && threadRes.ok) {
            const threadData = await threadRes.json();
            if (threadData.success && threadData.discussion) {
              const d = threadData.discussion;
              // Security: only the author can edit
              if (d.authorId !== currentUser.uid) {
                navigate('/community');
                return;
              }
              setTitle(d.title || '');
              // Strip HTML tags to get plain text back for the textarea
              setContent(d.contentHtml ? d.contentHtml.replace(/<[^>]*>/g, '').replace(/\n/g, '\n') : '');
              setCategoryId(d.communityId || '');
              setLakeId(d.lakeId || '');
              setTags(d.tags || []);
              setImageUrls(d.imageUrls || []);
              setRejectionReason(d.rejectionReason || null);
            }
          }
        }

        // Fetch categories
        const catRes = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/categories`).catch(() => null);
        let catData = null;
        if (catRes && catRes.ok) {
          catData = await catRes.json();
        }
        
        if (catData && catData.success && catData.categories.length > 0) {
          setCategories(catData.categories);
          if (!defaultCategoryId) setCategoryId(catData.categories[0].id);
        } else {
          // No categories available
          setCategories([]);
        }

        // Fetch lakes
        const lakeRes = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/lakes`).catch(() => null);
        let lakeData = null;
        if (lakeRes && lakeRes.ok) {
          lakeData = await lakeRes.json();
        }
        
        if (lakeData && lakeData.success && lakeData.lakes.length > 0) {
          setLakes(lakeData.lakes);
        } else {
          setLakes([]);
        }
      } catch (error) {
        console.error("Error fetching form data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFormData();
  }, [currentUser, navigate, defaultCategoryId]);

  const handleGrammarCheck = async () => {
    if (!content.trim()) return;
    setGrammarChecking(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/ai/grammar-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: content })
      });
      const data = await response.json();
      if (data.success && data.corrected) {
        setContent(data.corrected);
      }
    } catch (error) {
      console.error("Grammar check failed", error);
    } finally {
      setGrammarChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || !categoryId) return;

    setSubmitting(true);
    try {
      const endpoint = isEditMode && editId
        ? `${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions/${editId}`
        : `${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions`;

      const response = await fetch(endpoint, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          communityId: categoryId,
          lakeId: lakeId || null,
          title,
          contentHtml: `<p>${content
            .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-warning hover-gold text-decoration-underline">$1</a>')
            .replace(/\n/g, '<br/>')}</p>`,
          tags: tags,
          imageUrls: imageUrls
        })
      });

      const data = await response.json();
      
      if (data.success) {
        if (!isEditMode) localStorage.removeItem('bbr.community.draft');
        const defaultSuccessMsg = isEditMode
          ? "Your post has been resubmitted for review. You will be notified once a moderator approves it."
          : "Your discussion has been submitted! It will appear publicly once a moderator approves it. You will receive a notification.";
        
        setFeedbackModal({
          show: true,
          type: 'success',
          message: data.moderationMessage || defaultSuccessMsg,
          threadId: isEditMode ? editId : data.id,
          status: data.status
        });
      } else {
        setFeedbackModal({
          show: true,
          type: 'error',
          message: data.error || (isEditMode ? "Failed to update discussion" : "Failed to create discussion"),
          threadId: null
        });
      }
    } catch (error) {
      console.error("Submit failed", error);
      setFeedbackModal({
        show: true,
        type: 'error',
        message: "A network error occurred. Please check your connection and try again.",
        threadId: null
      });
    } finally {
      setSubmitting(false);
    }
  };



  if (loading) {
    return (
      <div className="community-container d-flex justify-content-center align-items-center">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  return (
    <div className="community-container">
      <Container>
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/community", className: "text-warning text-decoration-none hover-gold fw-bold" }}>
            Community Home
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            <span className="text-white fw-bold">
              {isEditMode ? 'Edit & Resubmit' : 'Start Discussion'}
            </span>
          </Breadcrumb.Item>
        </Breadcrumb>

        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <div className="community-card p-3 p-md-5 ig-style-card mb-5">
              <h2 className="text-warning fw-bold mb-4 border-bottom border-dark pb-3" style={{ fontSize: '1.5rem' }}>
                {isEditMode ? <><FaEdit className="me-2" />Edit &amp; Resubmit</> : 'Start a New Discussion'}
              </h2>

              {/* Show rejection reason context in edit mode */}
              {isEditMode && rejectionReason && (
                <Alert variant="warning" className="mb-4 rounded-3">
                  <strong>Moderator feedback:</strong> &ldquo;{rejectionReason}&rdquo;
                  <div className="small mt-1 text-dark">Please address the feedback above before resubmitting.</div>
                </Alert>
              )}
              
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label className="text-light fw-bold">Title <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    size="lg"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="What do you want to discuss?"
                    required
                    maxLength={150}
                    className="bg-dark text-white border-secondary"
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Group>

                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="text-light fw-bold">Category <span className="text-danger">*</span></Form.Label>
                      <Select
                        styles={selectStyles}
                        placeholder="Select a category..."
                        value={categories.filter(c => c.id === categoryId).map(c => ({ value: c.id, label: c.name }))[0] || null}
                        onChange={(selected) => setCategoryId(selected ? selected.value : '')}
                        options={categories.map(c => ({ value: c.id, label: c.name }))}
                        isClearable={false}
                        isSearchable={true}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="text-light fw-bold">Lake (Optional)</Form.Label>
                      <Select
                        styles={selectStyles}
                        placeholder="None / General"
                        value={lakes.filter(l => l.id === lakeId).map(l => ({ value: l.id, label: l.name }))[0] || null}
                        onChange={(selected) => setLakeId(selected ? selected.value : '')}
                        options={lakes.map(l => ({ value: l.id, label: l.name }))}
                        isClearable={true}
                        isSearchable={true}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-end mb-2 gap-2">
                    <Form.Label className="text-light fw-bold mb-0">Message <span className="text-danger">*</span></Form.Label>
                    <Button 
                      variant="outline-info" 
                      size="sm" 
                      onClick={handleGrammarCheck}
                      disabled={grammarChecking || !content.trim()}
                      className="align-self-start align-self-sm-end"
                      style={{ borderRadius: '6px' }}
                    >
                      {grammarChecking ? <Spinner size="sm" /> : <><FaMagic className="me-1"/> AI Grammar Check</>}
                    </Button>
                  </div>
                  {/* Mock Tiptap - basic textarea for now */}
                  <Form.Control
                    as="textarea"
                    rows={8}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Write your post here..."
                    required
                    className="bg-dark text-white border-secondary p-3"
                    style={{ borderRadius: '8px', minHeight: '150px' }}
                  />
                  <Form.Text className="text-muted mt-2 d-block">
                    Drafts are auto-saved to your browser. Please do not share personal contact info.
                  </Form.Text>
                </Form.Group>

                {/* Image Upload */}
                <Form.Group className="mb-4">
                  <Form.Label className="text-light fw-bold">Images (Optional)</Form.Label>
                  <div className="mb-2">
                    {/* Existing & newly uploaded image previews */}
                    {imageUrls.length > 0 && (
                      <div className="d-flex flex-wrap gap-3 mb-3">
                        {imageUrls.map((url, i) => (
                          <div key={i} style={{ position: 'relative', width: '100px', height: '100px' }}>
                            <img
                              src={url}
                              alt={`Upload ${i + 1}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px', border: '1px solid #444' }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setImageUrls(prev => prev.filter((_, idx) => idx !== i));
                              }}
                              style={{
                                position: 'absolute', top: '-8px', right: '-8px',
                                background: '#dc3545', border: 'none', borderRadius: '50%',
                                width: '26px', height: '26px', color: '#fff', fontSize: '14px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.5)'
                              }}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Upload zone */}
                    {imageUrls.length < 5 && (
                      <div
                        className="d-flex flex-column align-items-center justify-content-center text-center p-4"
                        style={{
                          border: '2px dashed #444',
                          borderRadius: '12px',
                          cursor: uploadingImages ? 'wait' : 'pointer',
                          background: 'rgba(255,255,255,0.02)',
                          transition: 'background 0.2s',
                          minHeight: '120px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                        onClick={() => !uploadingImages && document.getElementById('community-image-input').click()}
                      >
                        {uploadingImages ? (
                          <Spinner size="sm" variant="warning" className="mb-2" />
                        ) : (
                          <FaImage className="text-muted mb-2" style={{ fontSize: '2rem' }} />
                        )}
                        <span className="text-muted fw-bold">
                          {uploadingImages ? 'Uploading...' : 'Tap to add images'}
                        </span>
                        <span className="text-secondary small mt-1">
                          ({imageUrls.length}/5 uploaded)
                        </span>
                      </div>
                    )}
                    <input
                      id="community-image-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        if (files.length === 0) return;
                        const remaining = 5 - imageUrls.length;
                        const toUpload = files.slice(0, remaining);

                        // Validate file sizes (5MB max)
                        for (const f of toUpload) {
                          if (f.size > 5 * 1024 * 1024) {
                            alert(`${f.name} exceeds 5MB. Please choose a smaller image.`);
                            return;
                          }
                        }

                        setUploadingImages(true);
                        try {
                          const formData = new FormData();
                          toUpload.forEach(f => formData.append('files', f));
                          const res = await fetch(`${process.env.REACT_APP_BASE_URL}/api/upload-community-images`, {
                            method: 'POST',
                            body: formData,
                          });
                          const data = await res.json();
                          if (data.success) {
                            const newUrls = data.file_names.map(fn => `${process.env.REACT_APP_BASE_URL}/images/${fn}`);
                            setImageUrls(prev => [...prev, ...newUrls]);
                          }
                        } catch (err) {
                          console.error('Image upload failed:', err);
                        } finally {
                          setUploadingImages(false);
                          e.target.value = '';
                        }
                      }}
                    />
                  </div>
                  <Form.Text className="text-muted">
                    Up to 5 images. JPEG, PNG, or WebP. Max 5MB each.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="text-light fw-bold">Tags</Form.Label>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {tags.map((tag, index) => (
                      <div key={index} className="bg-warning text-dark rounded-pill px-3 py-1 d-flex align-items-center" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                        #{tag}
                        <span 
                          className="ms-2 text-dark d-flex align-items-center justify-content-center" 
                          style={{ cursor: 'pointer', fontSize: '1.2rem', lineHeight: '1', width: '20px', height: '20px', background: 'rgba(0,0,0,0.1)', borderRadius: '50%' }} 
                          onClick={() => setTags(tags.filter((_, i) => i !== index))}
                        >
                          &times;
                        </span>
                      </div>
                    ))}
                  </div>
                  <Form.Control
                    size="lg"
                    type="text"
                    placeholder="Type a tag and press Enter"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const newTag = tagInput.trim();
                        if (newTag && !tags.includes(newTag)) {
                          setTags([...tags, newTag]);
                        }
                        setTagInput('');
                      }
                    }}
                    className="bg-dark text-white border-secondary"
                    style={{ borderRadius: '8px' }}
                  />
                  <Form.Text className="text-muted mt-2 d-block">
                    Press Enter to add tags. Use specific keywords like "electronics" or "maintenance".
                  </Form.Text>
                </Form.Group>

                <div className="d-flex flex-column flex-sm-row justify-content-end gap-3 mt-5 pt-4 border-top border-dark">
                  <Link to="/community" className="btn btn-lg btn-outline-secondary" style={{ borderRadius: '8px' }}>Cancel</Link>
                  <Button variant="warning" type="submit" size="lg" disabled={submitting || !title || !content || !categoryId} style={{ borderRadius: '8px', fontWeight: 'bold' }}>
                    {submitting ? <Spinner size="sm" className="me-2"/> : <FaPaperPlane className="me-2" />}
                    {isEditMode ? 'Resubmit for Review' : 'Post Discussion'}
                  </Button>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Success / Error Feedback Modal */}
      <Modal
        show={feedbackModal.show}
        onHide={() => {
          setFeedbackModal(prev => ({ ...prev, show: false }));
          if (feedbackModal.type === 'success' && feedbackModal.threadId) {
            navigate(`/community/thread/${feedbackModal.threadId}`);
          }
        }}
        centered
        contentClassName="bg-dark border border-secondary"
      >
        <Modal.Body className="text-center py-5 px-4">
          {feedbackModal.type === 'success' ? (
            <FaCheckCircle style={{ fontSize: '3.5rem', color: '#f0a500' }} className="mb-4" />
          ) : (
            <FaExclamationCircle style={{ fontSize: '3.5rem', color: '#dc3545' }} className="mb-4" />
          )}
          <h4 className="text-white mb-3">
            {feedbackModal.type === 'success'
              ? (feedbackModal.status === 'published' 
                  ? (isEditMode ? 'Resubmitted Successfully!' : 'Discussion Published!') 
                  : 'Flagged by AI')
              : 'Something went wrong'}
          </h4>
          <p className="text-muted mb-4" style={{ lineHeight: '1.6' }}>
            {feedbackModal.message}
          </p>
          <Button
            variant={feedbackModal.type === 'success' ? 'warning' : 'outline-secondary'}
            onClick={() => {
              setFeedbackModal(prev => ({ ...prev, show: false }));
              if (feedbackModal.type === 'success' && feedbackModal.threadId) {
                navigate(`/community/thread/${feedbackModal.threadId}`);
              }
            }}
          >
            {feedbackModal.type === 'success' ? 'View My Post' : 'Close'}
          </Button>
          {feedbackModal.type === 'success' && (
            <div className="mt-3">
              <Button variant="link" className="text-muted small" onClick={() => navigate('/community')}>
                Return to Community Home
              </Button>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
